import { Injectable } from "@angular/core";
import { DataService } from "./data.service";
import { environment } from "src/environments/environment";
import { OldLabInfo } from "../models/old-labs.model";
import { normalizeOldLabInfo } from "../helpers/labs-panel.util";
import { catchError, map, Observable, of, tap, throwError } from "rxjs";
import { ApplicationCacheService } from "./application-cache.service";
import { ApiStatus, ReferralApis } from "./utils";
import { cloneDeep } from "lodash";
import { ApplicationContextService } from "./application-context.service";

@Injectable({
  providedIn: "root",
})
export class OldLabsService {
  constructor(
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _applicationContextService: ApplicationContextService
  ) {}

  getAppointmentLabs(appointmentId: string): Observable<OldLabInfo[]> {
    if (this.isEncounterLabsAlreadyLoaded) {
      return of(this.labsCache);
    }
    const url = this.apiBaseUrl + `api/v1/labs/encounter/${appointmentId}`;
    return this._dataService.get(url).pipe(
      map((labs: OldLabInfo[]) =>
        (labs || []).map((lab) => normalizeOldLabInfo(lab))
      ),
      tap((labs: OldLabInfo[]) => {
        this._applicationCacheService.setLabInfoCache(labs);
        this._applicationCacheService.setApiStatus(
          ApiStatus.Success,
          ReferralApis.Labs
        );
      }),
      catchError((error) => {
          this._applicationCacheService.setApiStatus(
            ApiStatus.Failed,
            ReferralApis.Labs
          );
        return throwError(() => error);
      })
    );
  }

  saveLabResults(payload: OldLabInfo) {
    const url = this.apiBaseUrl + `api/v1/labs`;
    const body = this.withUpdatedBy(payload) as OldLabInfo & {
      analyteResultFlags?: Record<string, string>;
    };
    if (payload.analyteResultFlags !== undefined) {
      body.analyteResultFlags = payload.analyteResultFlags;
    }
    return this._dataService.post(url, body).pipe(
      map((response: OldLabInfo) => this.mergeLabSaveResponse(body, response))
    );
  }

  private mergeLabSaveResponse(
    sent: OldLabInfo,
    response: OldLabInfo
  ): OldLabInfo {
    const merged = normalizeOldLabInfo({
      ...sent,
      ...response,
      labResults: response?.labResults ?? sent.labResults,
      documentId: response?.documentId ?? sent.documentId,
      clinicalOrderId: response?.clinicalOrderId ?? sent.clinicalOrderId,
      analyteResultFlags:
        response?.analyteResultFlags ?? sent.analyteResultFlags,
    });
    if (!merged.updatedAt && sent.updatedAt) {
      merged.updatedAt = sent.updatedAt;
    }
    if (!merged.updatedBy && sent.updatedBy) {
      merged.updatedBy = sent.updatedBy;
    }
    return merged;
  }

  private withUpdatedBy(payload: OldLabInfo): OldLabInfo {
    const body = cloneDeep(payload);
    const user = this._applicationContextService.contextSnapshot
      ?.currentlyLoggedInUser;
    const updatedBy = user
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : "";
    if (updatedBy) {
      body.updatedBy = updatedBy;
    }
    body.updatedAt = new Date().toISOString();
    return body;
  }

  deleteLab(id: string): Observable<void> {
    const url = this.apiBaseUrl + `api/v1/labs/${id}`;
    return this._dataService.delete(url).pipe(
      tap(() => {
        const labs = this.labsCache.filter((lab) => lab.id !== id);
        this._applicationCacheService.setLabInfoCache(labs);
      }),
      catchError((error) => throwError(() => error))
    );
  }

  get isEncounterLabsAlreadyLoaded(): boolean {
    return (
      this._applicationCacheService.getApiStatus(ReferralApis.Labs) ===
      ApiStatus.Success
    );
  }

  get labsCache(): OldLabInfo[] {
    return cloneDeep(this._applicationCacheService.applicationCache.labs);
  }

  get apiBaseUrl(): string {
    return environment.apiBaseUrl;
  }
}
